import { ReactElement, ReactNode, RefObject } from 'react';
import { Pressable, View } from 'react-native';
import { Heading } from '@/common/components/Heading/Heading';
import { IconBadge } from '@/common/components/IconBadge/IconBadge';
import { Icon, TIconProps } from '@/common/components/Icons/Icon';
import { useOverlay } from '@/common/components/Overlay/Overlay.context';
import {
  TOverlayChildren,
  TOverlayProps,
  TOverlayRef,
} from '@/common/components/Overlay/Overlay.types';
import { OverlayBottom } from '@/common/components/Overlay/OverlayBottom/OverlayBottom';
import { OverlayCenter } from '@/common/components/Overlay/OverlayCenter/OverlayCenter';
import { OverlayChildren } from '@/common/components/Overlay/OverlayChildren/OverlayChildren';
import { Stack } from '@/common/components/Stack/Stack';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { cn } from '@/common/theme/tv.config';
import { TColors } from '@coverflex-tech/hypernova-design-tokens';
import { styles } from './Modal.styles';

export function Modal({
  icon,
  iconBadgeColor,
  title,
  children,
  footer,
  contentPadding = 'lg',
  closeOnX = true,
  height = 'auto',
  heightClass,
  horizontalPosition = 'center',
  headerProps,
  footerProps,
  modalRef,
  name = 'modal',
  ...overlayProps
}: TModalProps) {
  const breakpoints = useBreakpoints(['sm']);

  if (breakpoints.sm) {
    return (
      <OverlayCenter
        {...overlayProps}
        name={name}
        header={
          <ModalHeader
            icon={icon}
            iconBadgeColor={iconBadgeColor}
            title={title}
            closeOnX={closeOnX}
            className={headerProps?.className}
          />
        }
        footer={footer && <ModalFooter {...footerProps}>{footer}</ModalFooter>}
        height={height}
        horizontalPosition={horizontalPosition}
        className={cn(overlayProps.className, heightClass)}
        ref={modalRef}
      >
        <ModalContent contentPadding={contentPadding}>{children}</ModalContent>
      </OverlayCenter>
    );
  }

  return (
    <OverlayBottom
      {...overlayProps}
      name={name}
      fixedHeight={!!heightClass}
      ref={modalRef}
      header={
        <>
          <View className={styles.handle} />
          <ModalHeader
            icon={icon}
            iconBadgeColor={iconBadgeColor}
            title={title}
            closeOnX={closeOnX}
            className={headerProps?.className}
          />
        </>
      }
      footer={
        footer ? (
          <ModalFooter {...footerProps}>{footer}</ModalFooter>
        ) : undefined
      }
    >
      <ModalContent contentPadding={contentPadding}>{children}</ModalContent>
    </OverlayBottom>
  );
}

export const ModalHeader = ({
  title,
  icon,
  iconBadgeColor,
  closeOnX,
  className,
}: TModalHeaderProps) => {
  const { close } = useOverlay();
  return title ? (
    <Stack
      horizontal
      align="center"
      justify="between"
      gap="md"
      className={styles.header({ className })}
    >
      <Stack horizontal align="center" gap="sm" className={styles.shrink}>
        {icon && (
          <IconBadge
            icon={icon}
            ratio={48}
            backgroundColor={iconBadgeColor}
            className={styles.icon}
          />
        )}
        <Heading level={3} className={styles.shrink}>
          {title}
        </Heading>
      </Stack>
      {closeOnX && (
        <Pressable onPress={close}>
          <Icon icon={['far', 'xmark']} size={24} color="neutral20" />
        </Pressable>
      )}
    </Stack>
  ) : (
    <Stack
      horizontal
      align="center"
      justify="end"
      className={styles.header({ absolute: true, className })}
    >
      {closeOnX && (
        <Pressable onPress={close}>
          <Icon icon={['far', 'xmark']} size={24} color="neutral20" />
        </Pressable>
      )}
    </Stack>
  );
};

const ModalContent = ({ children, contentPadding }: TModalContentProps) => {
  return (
    <View className={styles.content({ contentPadding })}>
      <OverlayChildren>{children}</OverlayChildren>
    </View>
  );
};

const ModalFooter = ({
  border = true,
  className,
  children,
}: TModalFooterProps) => {
  return (
    <View className={styles.footer({ border, className })}>
      <OverlayChildren>{children}</OverlayChildren>
    </View>
  );
};

export interface TModalProps
  extends Omit<TOverlayProps, 'rootId' | 'ref' | 'header' | 'footer'> {
  icon?: ReactElement<TIconProps>;
  iconBadgeColor?: keyof TColors['light']['semantic']['surface'];
  title?: ReactNode;
  contentPadding?: 'none' | 'lg' | 'xl' | '2xl';
  closeOnX?: boolean;
  footer?: TOverlayChildren;
  headerProps?: Pick<TModalHeaderProps, 'className'>;
  footerProps?: Omit<TModalFooterProps, 'children'>;
  overflowY?: 'none' | 'auto';
  heightClass?: `h-${string}`;
  modalRef?: RefObject<TOverlayRef | null>;
}

export type TModalHeaderProps = Pick<
  TModalProps,
  'icon' | 'iconBadgeColor' | 'title' | 'closeOnX' | 'className'
>;

export type TModalContentProps = {
  contentPadding?: 'none' | 'lg' | 'xl' | '2xl';
  children: TOverlayChildren;
};

export type TModalFooterProps = {
  border?: boolean;
  children?: TOverlayChildren;
  className?: string;
};
